// Decision 4 of docs/plans/2026-08-13-verification-integration-closeout.md,
// made executable: what happens to playback when something does not verify.
//
// The policy itself is the pure function `applyVerificationOutcome` at the top
// of this file. It is a reducer over the outcomes the four verifiers report,
// and it is where the whole decision table lives:
//
//   MISMATCH on sealed content  -> halt. Terminal for the session, never
//                                  retried, never cleared.
//   NETWORK / decode failure    -> nothing. Playback continues and the
//                                  existing player/streamError.ts path is the
//                                  only thing that reacts.
//   ADDENDUM failing its hash   -> dropped from the picker. Never halts: an
//                                  addendum is a weaker, separate claim, and
//                                  letting one stop sealed content would
//                                  invert the trust model.
//   ROOT not verified yet       -> nothing is checked and nothing is stopped.
//                                  There is nothing trustworthy to compare
//                                  against, so there is no evidence to act on.
//
// Two things about the shape of this are load-bearing rather than stylistic:
//
//  * IT BRANCHES ON `kind`, NEVER ON WORDING. T13 made 'network' | 'mismatch'
//    a real field on every failure precisely so this file can tell "the bytes
//    changed" from "we never got the bytes" without matching strings. A
//    reworded detail message must never change what playback does.
//
//  * IT RETURNS THE SAME OBJECT WHEN NOTHING CHANGED. A CDN blip, an aborted
//    segment, a passing hash — none of them alter the session, so none of them
//    re-render anything downstream. That is the containment Decision 4
//    describes ("a failed fetch is not a mismatch") expressed as identity.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useConnection } from '@solana/wallet-adapter-react'
import { PublicKey } from '@solana/web3.js'
import {
  FORUM_IDL,
  FORUM_PROGRAM_ID,
  langBytes,
  publisherPda,
  subtitlePda,
  videoIdBytes,
  videoPda,
  type SubtitleTrackAccount,
} from '../solana/forum/index.js'
import { useProgramReader } from '../solana/react/index.js'
import type { Video } from '../types.js'
import { SegmentVerifier, type SegmentVerificationResult } from './SegmentVerifier.js'
import { useChainRecord, type AnchorState, type ChainRecord } from './useChainRecord.js'
import {
  computeVersionConsistency,
  useContainerRecord,
  type ContainerRecord,
  type VersionConsistency,
} from './useContainerRecord.js'
import { verifyRendition, type RenditionVerified, type VerifyRenditionResult } from './verifyRendition.js'
import { verifyRoot, type RootVerified, type VerifyRootResult } from './verifyRoot.js'
import { verifyAddendumSubtitle, verifyBaseSubtitle, type VerifySubtitleResult } from './verifySubtitle.js'

export type VerifyState = 'idle' | 'verifying' | 'verified' | 'failed'

/**
 * Why playback stopped. Matches `VerificationResult.failure` in the plan's
 * Interfaces section, with one addition: `language`, because a base subtitle
 * track is sealed content and can halt, and the plan's four fields left no
 * way to say which language did.
 */
export interface VerificationHalt {
  stage: 'root' | 'rendition' | 'segment' | 'subtitle'
  detail: string
  segmentIndex?: number
  rendition?: string
  language?: string
}

/**
 * Which authority an outcome was checked against — and therefore what it is
 * evidence of. The `layer` is not derivable from the result: a base subtitle
 * failure and an addendum subtitle failure are the identical shape, and one
 * halts a video while the other quietly drops a menu entry. Only the caller
 * that performed the check knows which hash it compared against (Decision 2:
 * the sealed base and its addenda have different verification authorities),
 * so that knowledge is carried here rather than sniffed out later.
 */
export type VerificationOutcome =
  | { layer: 'root'; result: VerifyRootResult }
  | { layer: 'rendition'; result: VerifyRenditionResult }
  | { layer: 'segment'; result: SegmentVerificationResult }
  | { layer: 'baseSubtitle'; result: VerifySubtitleResult }
  | { layer: 'addendumSubtitle'; language: string; result: VerifySubtitleResult }

export interface PlaybackPolicy {
  /** Set once root.json has been checked against the chain's root_hash and
   * agreed. Nothing below is checked, and nothing is halted, before this. */
  rootVerified: boolean
  /** Set when the root could not be verified at all, for either reason. The
   * badge reads `failed`; playback is untouched. */
  rootFailed: boolean
  /** Non-null once the session is terminally halted. Never cleared, never
   * replaced by a later failure — the first mismatch is the one reported. */
  halt: VerificationHalt | null
  /** Addendum languages whose bytes failed their own on-chain hash. Dropped
   * from the subtitle picker by T18. Not a reason to stop anything. */
  rejectedAddenda: string[]
  /**
   * Where the version being played sits in the container's on-chain version
   * list. Written only by `withVersionConsistency`, never by
   * `applyVerificationOutcome` — `VersionConsistency` is deliberately not a
   * member of `VerificationOutcome`, so no call exists that could route it
   * into `haltFor`. A superseded version's bytes hashed correctly against the
   * root the publisher sealed; they are authentic, and only bytes that hash
   * wrong stop a player. This is a warning surface, and it is one by
   * construction rather than by convention.
   */
  versionConsistency: VersionConsistency
}

// Frozen because it is a module-level singleton several sessions share: every
// transition below builds a new object rather than writing to one, and this
// makes a slip that broke that rule fail loudly instead of leaking one page's
// rejected addenda into the next.
export const INITIAL_POLICY: PlaybackPolicy = Object.freeze({
  rootVerified: false,
  rootFailed: false,
  halt: null,
  rejectedAddenda: [],
  // Nothing has been read from the chain yet, so no claim is made about which
  // version this is. `unanchored` would be a claim — that the chain holds no
  // container — and we have not looked.
  versionConsistency: 'unknown' as VersionConsistency,
})

// D11: every layer that touches a language code canonicalizes it first, so
// "DE" and "de" are never two different tracks. Same rule as T5's normalizeLang
// in api/pkg/data and T14's in verifySubtitle.ts.
const normalizeLang = (lang: string): string => lang.trim().toLowerCase()

/**
 * The policy. Pure, total, and the single place playback is ever stopped.
 *
 * Returns `policy` itself — the same reference — whenever an outcome changes
 * nothing, which is the overwhelmingly common case.
 */
export function applyVerificationOutcome(policy: PlaybackPolicy, outcome: VerificationOutcome): PlaybackPolicy {
  // Terminal. A halted session does not un-halt, does not re-report, and does
  // not accumulate a second reason — "not retried into a loop" is this line.
  if (policy.halt) return policy

  if (outcome.layer === 'addendumSubtitle') {
    // An addendum never halts, whatever it did and whatever state the base is
    // in. Only a hash disagreement condemns the track: a 503 says nothing
    // about the bytes, so it is not grounds for dropping a publisher's
    // translation from the picker.
    if (outcome.result.ok || outcome.result.kind !== 'mismatch') return policy
    const language = normalizeLang(outcome.language)
    if (policy.rejectedAddenda.includes(language)) return policy
    return { ...policy, rejectedAddenda: [...policy.rejectedAddenda, language] }
  }

  if (outcome.layer === 'root') {
    if (outcome.result.ok) {
      return policy.rootVerified ? policy : { ...policy, rootVerified: true }
    }
    // An unverifiable root reads `failed` and stops nothing. Decision 4: there
    // is nothing trustworthy to compare against, so nothing below is attempted
    // and no claim is made about the bytes that are playing.
    return policy.rootFailed ? policy : { ...policy, rootFailed: true }
  }

  // Everything from here is sealed base content: a rendition manifest, a media
  // segment, a base subtitle track. All three are covered by the root hash the
  // chain holds, so a disagreement is evidence about the seal itself.
  if (outcome.result.ok) return policy
  if (outcome.result.kind !== 'mismatch') return policy
  if (!policy.rootVerified) return policy

  return { ...policy, halt: haltFor(outcome) }
}

/**
 * The only way `versionConsistency` is ever written. Separate from
 * `applyVerificationOutcome` on purpose: the reducer takes evidence about
 * BYTES and can halt a session, and a version-list disagreement is not
 * evidence about bytes. Keeping the two entry points apart is what makes
 * "a superseded version never halts playback" a property of the code's shape
 * rather than of a branch someone has to keep remembering not to add.
 *
 * Returns `policy` itself when nothing changed, for the same reason the
 * reducer does — see the identity note at the top of this file.
 */
export function withVersionConsistency(policy: PlaybackPolicy, consistency: VersionConsistency): PlaybackPolicy {
  if (policy.versionConsistency === consistency) return policy
  return { ...policy, versionConsistency: consistency }
}

function haltFor(outcome: VerificationOutcome): VerificationHalt {
  switch (outcome.layer) {
    case 'segment': {
      const failure = outcome.result as Extract<SegmentVerificationResult, { ok: false }>
      return {
        stage: 'segment',
        detail: failure.detail,
        rendition: failure.rendition,
        segmentIndex: failure.segmentIndex,
      }
    }
    case 'rendition': {
      const failure = outcome.result as Extract<VerifyRenditionResult, { ok: false }>
      return { stage: 'rendition', detail: failure.detail, rendition: failure.rendition }
    }
    default: {
      const failure = outcome.result as Extract<VerifySubtitleResult, { ok: false }>
      return { stage: 'subtitle', detail: failure.detail, language: failure.language }
    }
  }
}

/**
 * Whether media segments should be hashed at all. False before the root has
 * verified — Decision 4's "no segment checking is attempted" — and false after
 * a halt, because the session is over and more evidence changes nothing.
 */
export function shouldCheckSegments(policy: PlaybackPolicy): boolean {
  return policy.rootVerified && policy.halt === null
}

/**
 * The badge state (Decision 5's verification axis) implied by the policy.
 * `outstanding` is how many checks of the sealed base have not come back yet;
 * streaming segment checks are not counted, because a video is verified once
 * its sealed base is, and later segments can only ever move it to `failed`.
 */
export function verifyStateFor(policy: PlaybackPolicy, anchor: AnchorState, outstanding: number): VerifyState {
  if (policy.halt || policy.rootFailed) return 'failed'
  if (anchor === 'unanchored') return 'idle'
  if (!policy.rootVerified) return 'verifying'
  return outstanding > 0 ? 'verifying' : 'verified'
}

/**
 * What failed, in words a viewer can hold — "segment 2 of the 1080p
 * rendition". Lives here rather than in the surface that renders it so the
 * mapping from failure data to a phrase is testable without a renderer, and
 * so the awkward shapes are handled once: T15 notes that a playlist-level
 * disagreement carries a rendition but NO segment index, and that is a case
 * the failure surface has to render rather than print "segment undefined".
 */
export function describeSealedTarget(halt: VerificationHalt): string {
  switch (halt.stage) {
    case 'segment':
      if (!halt.rendition) return 'One of this video’s media segments'
      if (typeof halt.segmentIndex !== 'number') return `The ${halt.rendition} rendition’s segment list`
      return `Segment ${halt.segmentIndex} of the ${halt.rendition} rendition`
    case 'rendition':
      return halt.rendition ? `The ${halt.rendition} rendition’s manifest` : 'A rendition manifest'
    case 'subtitle':
      return halt.language ? `The ${halt.language.toUpperCase()} subtitles` : 'A subtitle track'
    case 'root':
      return 'This video’s manifest'
  }
}

// ---------------------------------------------------------------------------
// The hook
// ---------------------------------------------------------------------------

export interface VerificationResult {
  verify: VerifyState
  anchor: AnchorState
  chain: ChainRecord | null
  /** Why playback stopped, or null. Pass to VideoPlayer's
   * `verificationFailure` prop, which is what halts it and draws the scrim;
   * VerifiedPlayer also turns it into the panel's `mismatch` (failureSurface.ts)
   * so the halted viewer is told why. */
  failure: VerificationHalt | null
  /** Addendum languages whose bytes failed their on-chain hash — dropped from
   * the picker (T18). */
  rejectedAddenda: string[]
  /** Whether the version being played is the container's current one,
   * according to the chain. A warning surface only — see PlaybackPolicy. */
  versionConsistency: VersionConsistency
  /**
   * The container exactly as the chain holds it, or null when it was never
   * anchored, could not be read, or there was nothing to look up.
   *
   * Handed back rather than kept private because the verification panel lists
   * these versions and this hook has already read them. A second
   * `useContainerRecord` in the panel's own tree would open a second
   * getAccountInfo and a second websocket subscription on the same account to
   * learn the same thing. It is deliberately NOT on `PlaybackPolicy`: nothing
   * about the version list may reach `applyVerificationOutcome`, and keeping
   * it out of the policy object is what makes that structural.
   */
  container: ContainerRecord | null
  /**
   * The inline segment checker to hand to VideoPlayer, or undefined when there
   * is nothing to check against. Beyond the plan's Interfaces sketch, but the
   * page has to get one from somewhere and this hook is what knows the root
   * has verified — the SegmentVerifier constructor demands a `RootVerified`.
   */
  verifier: SegmentVerifier | undefined
  /**
   * The sealed base, once the sweep has established it: the root manifest this
   * page checked against the chain, and every rendition manifest under it that
   * verified. Both are null/empty until then, and both are cleared the moment
   * `videoId` changes, so one video's lanes can never survive a version
   * navigation.
   *
   * Handed back for one reason: the ledger (ledger/lanes.ts) needs exactly
   * these two to build its lanes, and this hook is the only thing that has
   * them. They were locals inside the sweep before T14 of
   * docs/plans/2026-09-07-verification-overlay.md.
   *
   * They are set at the SAME moment `verifier` is — i.e. only when segments
   * are going to be hashed at all. A sweep that halted before that point has
   * nothing to record into lanes, and an empty chart under a failure block
   * would say less than the failure block already does.
   */
  root: RootVerified | null
  renditions: RenditionVerified[]
}

/** Root and renditions move together or not at all — they are two halves of
 * one fact, and `buildLanes` reads them in the same breath. One state slot,
 * so no render can ever observe a root with the wrong rendition list. */
interface SealedBase {
  root: RootVerified | null
  renditions: RenditionVerified[]
}

/** The sealed base before the sweep has found one. A module constant so its
 * identity is stable: resetting to it must not look like a change to the
 * memo below, and an empty rendition list must not be a new array each time. */
const NO_SEALED_BASE: SealedBase = Object.freeze({ root: null, renditions: [] })

/**
 * Verifies a video against the chain, for a viewer with no wallet and no
 * account, and decides what that means for playback.
 *
 * Order matters and is the trust model in sequence: read the chain, check
 * root.json against what the chain sealed, and only then check anything the
 * root names. The API is never asked whether it is telling the truth; it is
 * only asked where the bytes are.
 *
 * `containerId` is one more pointer of the same kind: which container's
 * version list this video should be found in. It is optional because a page
 * that has not resolved one yet still gets a working player — the version
 * axis simply reads `unknown`, which is what not having looked means.
 *
 * `containerAuthority` is the wallet the CONTAINER was anchored under, from
 * the container JSON the API served. It exists because the video's own
 * authority is not always available: a version that was never anchored has no
 * `publication.chain` at all, and that is precisely the case where the
 * chain's version list most needs consulting. Without this argument such a
 * page could not derive the container PDA, so it could never learn that the
 * chain does not name what it is playing.
 *
 * `onSegmentResult` is a pure OBSERVER of the segment results this hook is
 * already receiving — the verification ledger, which draws them. It is called
 * after the policy has seen the same result and can change nothing about what
 * playback does; it exists so the display does not need a second verifier
 * hashing the same bytes twice.
 */
export function useVideoVerification(
  video: Video,
  containerId?: string,
  containerAuthority?: string,
  onSegmentResult?: (result: SegmentVerificationResult) => void,
): VerificationResult {
  const { chain } = useChainRecord(video)
  const { connection } = useConnection()
  const reader = useProgramReader(FORUM_IDL, FORUM_PROGRAM_ID)
  const readerRef = useRef(reader)
  readerRef.current = reader

  // The policy is kept in a ref as well as in state so the sweep below can see
  // its own effect immediately: `report` is called between awaits, and a halt
  // must stop the next await from running rather than wait for a re-render.
  const policyRef = useRef<PlaybackPolicy>(INITIAL_POLICY)
  const [policy, setPolicy] = useState<PlaybackPolicy>(INITIAL_POLICY)
  const [outstanding, setOutstanding] = useState(0)
  const [verifier, setVerifier] = useState<SegmentVerifier | undefined>(undefined)
  const [sealedBase, setSealedBase] = useState<SealedBase>(NO_SEALED_BASE)

  // The ledger sink, held in a ref rather than taken as an effect dependency.
  // The sweep below is keyed on [videoId, rootHash, report] and must NOT be
  // re-run because a caller passed a new closure: re-running it would build a
  // second SegmentVerifier and re-fetch the sealed base. A ref keeps the
  // callback current without touching what causes a sweep.
  const onSegmentResultRef = useRef(onSegmentResult)
  onSegmentResultRef.current = onSegmentResult

  const report = useCallback((outcome: VerificationOutcome) => {
    const next = applyVerificationOutcome(policyRef.current, outcome)
    // Identity, not equality: an unchanged policy must not re-render the page
    // that renders the player.
    if (next === policyRef.current) return
    policyRef.current = next
    setPolicy(next)
  }, [])

  const videoId = video.id
  const rootHash = chain?.rootHash ?? null
  const authority = video.publication?.chain?.authority ?? null
  // Joined into a string so the effect's dependency is a value rather than an
  // array identity that changes on every react-query refetch.
  const addendumLangs = (video.publication?.addenda ?? []).map((track) => normalizeLang(track.lang)).join(',')

  // The sealed base: root, then every rendition the root names, then every
  // base subtitle it names. Sequential on purpose — a halt must stop the
  // sweep, and a mismatch found early makes the rest moot.
  useEffect(() => {
    // Reset everything the sweep is about to re-establish, but carry the
    // version axis across: it is answered by a different read, on a different
    // schedule, and its effect below only fires when the computed value
    // changes. Resetting it to `unknown` here would strand it there whenever
    // the new video's consistency happens to equal the old one's.
    const reset = withVersionConsistency(INITIAL_POLICY, policyRef.current.versionConsistency)
    policyRef.current = reset
    setPolicy(reset)
    setVerifier(undefined)
    // Cleared on every videoId (or rootHash) change, before anything is read.
    // This is what stops the previous video's lanes outliving it.
    setSealedBase(NO_SEALED_BASE)
    if (!rootHash) {
      setOutstanding(0)
      return
    }

    let live = true
    setOutstanding(1)

    void (async () => {
      const root = await verifyRoot(videoId, rootHash)
      if (!live) return
      report({ layer: 'root', result: root })
      if (!root.ok) {
        setOutstanding(0)
        return
      }

      const renditions: RenditionVerified[] = []
      for (const ref of root.manifest.renditions) {
        const result = await verifyRendition(videoId, ref)
        if (!live) return
        report({ layer: 'rendition', result })
        if (result.ok) renditions.push(result)
        if (policyRef.current.halt) break
      }

      if (!policyRef.current.halt) {
        for (const ref of root.manifest.subtitles) {
          const result = await verifyBaseSubtitle(videoId, ref)
          if (!live) return
          report({ layer: 'baseSubtitle', result })
          if (policyRef.current.halt) break
        }
      }

      if (!live) return
      setOutstanding(0)
      // Nothing to hash segments against once the session is over, and the
      // constructor's RootVerified requirement is the other half of the same
      // precondition.
      if (shouldCheckSegments(policyRef.current)) {
        // The ledger's lanes are built from these, and they are published
        // BEFORE the verifier that will report into them: `useVerificationLedger`
        // derives its LaneSet during render, so by the time this verifier is
        // attached to the player by an effect, the lanes it reports into exist.
        setSealedBase({ root, renditions })
        setVerifier(
          new SegmentVerifier({
            root,
            renditions,
            onResult: (result) => {
              if (!live) return
              // The POLICY FIRST, always. It is what halts playback, and it
              // must never be able to be pre-empted or skipped by a display
              // concern. The ledger is told second, and only ever records.
              report({ layer: 'segment', result })
              onSegmentResultRef.current?.(result)
            },
          }),
        )
      }
    })()

    return () => {
      live = false
    }
  }, [videoId, rootHash, report])

  // Addenda, verified against their OWN on-chain accounts (Decision 2). This
  // runs independently of the sweep above and can never halt anything, so it
  // is deliberately not sequenced with it: a slow chain read must not delay
  // the sealed base's verdict.
  useEffect(() => {
    const languages = addendumLangs ? addendumLangs.split(',') : []
    if (languages.length === 0 || !authority) return

    let derivedVideoPda: PublicKey
    try {
      derivedVideoPda = videoPda(publisherPda(new PublicKey(authority)), videoIdBytes(videoId))
    } catch {
      // A malformed authority or video id derives nothing. The base video's
      // own chain read (useChainRecord) reports that; there is no second
      // error to raise here.
      return
    }

    let live = true
    void (async () => {
      for (const lang of languages) {
        let account: SubtitleTrackAccount
        try {
          const info = await connection.getAccountInfo(subtitlePda(derivedVideoPda, langBytes(lang)))
          if (!live) return
          // No account: the track was never anchored, so there is no on-chain
          // hash to check it against. Unverified is not the same as failed —
          // see D17 — so it is neither rejected nor halted here.
          if (!info) continue
          account = readerRef.current.coder.accounts.decode<SubtitleTrackAccount>('subtitleTrack', info.data)
        } catch {
          continue
        }
        const result = await verifyAddendumSubtitle(videoId, lang, account)
        if (!live) return
        report({ layer: 'addendumSubtitle', language: lang, result })
      }
    })()

    return () => {
      live = false
    }
  }, [videoId, authority, addendumLangs, connection, report])

  // Which authority to derive the container PDA from. The video's own is
  // preferred when there is one — it is the authority this page has already
  // been reading under — and the container's stands in when there is not,
  // which is exactly the unanchored-version case the version axis exists for.
  //
  // WHEN THE TWO ARE PRESENT AND DISAGREE, NEITHER IS USED. A container
  // anchored under a different wallet from the video being played is not a
  // pointer worth following: it would derive some other publisher's container
  // and compare this video against a list it was never meant to appear in.
  // `unknown` — "not looked up" — is the honest answer there.
  const containerReadAuthority = useMemo(() => {
    if (!authority) return containerAuthority
    if (!containerAuthority) return authority
    return authority === containerAuthority ? authority : undefined
  }, [authority, containerAuthority])

  // The version list, read wallet-free. It cannot halt anything; it only ever
  // moves the field below.
  const {
    record: containerRecordRead,
    missing: containerMissing,
    error: containerReadError,
  } = useContainerRecord(containerId, containerReadAuthority)
  // containerReadError is whichever chain read actually failed — the
  // container account's own read, or the refs read, whichever one
  // useForumContainer's `containerState.error ?? versionsState.error` picked
  // up (see ContainerReadState.error) — and either way it is a gap in what we
  // could read, not a fact about the chain's list. computeVersionConsistency's
  // own rule already treats a short versions[] this way for a rejected refs
  // fetch (that leaves versions[] at []), but a failed CONTAINER read needs
  // this extra check: it would otherwise leave versions[] looking identical
  // to "container simply has none yet". Checking the error here, at the point
  // the hook reads it, means a caller of `container` below never sees a
  // record built from a read that errored out from under it.
  const containerRecord = containerReadError ? null : containerRecordRead
  const versionConsistency = computeVersionConsistency(containerRecord, containerMissing, videoId)

  useEffect(() => {
    const next = withVersionConsistency(policyRef.current, versionConsistency)
    if (next === policyRef.current) return
    policyRef.current = next
    setPolicy(next)
  }, [versionConsistency])

  const anchor: AnchorState = chain?.state ?? 'unanchored'

  return useMemo(
    () => ({
      verify: verifyStateFor(policy, anchor, outstanding),
      anchor,
      chain,
      failure: policy.halt,
      rejectedAddenda: policy.rejectedAddenda,
      versionConsistency: policy.versionConsistency,
      container: containerRecord,
      verifier,
      root: sealedBase.root,
      renditions: sealedBase.renditions,
    }),
    [policy, anchor, chain, outstanding, verifier, containerRecord, sealedBase],
  )
}
